import { isApiEnabled } from '@muzly/api-client';

import { listDone } from './analyTransport';
import { fromAnalyView } from './fromAnalyView';
import { loadProfile } from '../../profile';
import { listDiagnoses, saveDiagnosis } from '../record';

import type { AnalyView } from './analyContract';
import type { OnboardingAnswer } from '../flow';
import type { DiagnosisRecord } from '../record';

/**
 * restoreDiagnoses — 로컬 이력이 **비었을 때 한 번만**, 서버의 done 회차를 읽어 로컬에
 * 채워 넣는다(CONTEXT.md "Restore").
 *
 * `profile-restore.ts`와 같은 모양이다: deps 주입, 실패·지연 모두 조용히(throw 없음),
 * 총 상한(`timeoutMs`) 안에서만 기다린다. 다른 점은 하나 — 이쪽은 값을 돌려주지 않고
 * (`void`) 저장까지 이 함수 안에서 끝낸다. 호출부(`useDiagnosisRestore`)는 결과를 몰라도
 * 되고, 화면은 `useDiagnoses()` 구독으로 저장된 것을 본다.
 *
 * **로컬이 정본**이라 게이트를 **두 번** 본다 — 시작할 때 한 번(요청 자체를 걸지 말지),
 * 서버 응답을 받은 뒤 저장 직전 한 번 더(그 사이에 이 기기에서 진단을 마쳤을 수 있다).
 * 두 번째 게이트가 없으면 방금 생긴 로컬 회차 위에 서버 회차가 섞여 "로컬이 정본"이 깨진다.
 *
 * **로컬 프로필도 저장 직전에 다시 본다, 없으면 이번엔 포기한다.** `app.tsx`는 이 훅을
 * `RequireProfile` 게이트 **앞**(`AppBody`, 무조건 실행)에 둔다 — 프로필 게이트 자신의
 * 서버 복원도 같은 타이밍에 같은 2초 상한으로 도는 별개 요청이라, 재설치 직후 첫 실행에서
 * 이 함수가 먼저 끝나면 프로필이 아직 로컬에 없을 수 있다. 그때 `survey: {}`로 저장하면
 * 그 회차들은 다시는 복원되지 않으므로(로컬이 채워지면 게이트가 영구히 막힌다) 영영
 * 성별 없이 남는다. 없으면 저장하지 않고 다음 실행에 다시 시도한다(로컬 이력이 계속
 * 비어 있으니 게이트가 다시 열린다) — plan Decisions Q5의 "게이트 뒤라 이미 있다"는
 * 이 배선(`useProfileSync` 옆, 게이트 앞)에서는 성립하지 않는다.
 */

export const RESTORE_DIAGNOSES_TIMEOUT_MS = 2_000;

export interface RestoreDiagnosesDeps {
  enabled: () => boolean;
  listDiagnoses: typeof listDiagnoses;
  listDone: () => Promise<AnalyView[] | null>;
  loadProfile: () => { answers: Record<string, OnboardingAnswer> } | null;
  saveDiagnosis: typeof saveDiagnosis;
  timeoutMs: number;
}

const defaultDeps = (): RestoreDiagnosesDeps => ({
  enabled: isApiEnabled,
  listDiagnoses,
  listDone,
  loadProfile,
  saveDiagnosis,
  timeoutMs: RESTORE_DIAGNOSES_TIMEOUT_MS,
});

export const restoreDiagnoses = async (
  deps: RestoreDiagnosesDeps = defaultDeps(),
): Promise<void> => {
  // 실패는 전부 조용히 끝난다(profile-restore.ts와 같은 계약) — deps 중 무엇이 던져도
  // 이 함수는 던지지 않는다. 호출부(`useDiagnosisRestore`)가 결과를 기다리지 않고 등록만
  // 하기 때문에, 여기서 새는 예외는 unhandled rejection이 된다.
  try {
    if (!deps.enabled() || deps.listDiagnoses().length > 0) {
      return;
    }

    let timer: ReturnType<typeof setTimeout> | undefined;
    const timeout = new Promise<null>((resolve) => {
      timer = setTimeout(() => resolve(null), deps.timeoutMs);
    });
    let views: AnalyView[] | null;
    try {
      views = await Promise.race([deps.listDone(), timeout]);
    } catch {
      views = null;
    } finally {
      clearTimeout(timer);
    }
    if (!views || views.length === 0) {
      return;
    }

    // 두 번째 게이트(모듈 헤더 주석) — 기다리는 동안 이 기기에서 진단을 마쳤을 수 있다.
    if (deps.listDiagnoses().length > 0) {
      return;
    }

    // 세 번째 게이트(모듈 헤더 주석) — 프로필 게이트의 서버 복원과 경합한다. 아직 없으면
    // 빈 설문으로 박지 않고 이번엔 포기한다(다음 실행이 다시 시도한다).
    const answers = deps.loadProfile()?.answers;
    if (answers === undefined) {
      return;
    }

    const records = views
      // 소프트 삭제된 행을 목록이 돌려줘도 되살리지 않는다.
      .filter((view) => !view.deletedAt)
      .map((view) => fromAnalyView(view, answers))
      .filter((record): record is DiagnosisRecord => record !== null)
      // 오래된 것부터 저장한다 — `saveDiagnosis`는 매번 인덱스 맨 앞에 얹으므로, 오래된
      // 순으로 넣어야 마지막에 저장한(가장 최신) 회차가 맨 앞에 남아 최신순이 유지된다.
      .sort((a, b) => a.completedAt - b.completedAt);

    for (const record of records) {
      deps.saveDiagnosis(record);
    }
  } catch {
    /* profile-restore.ts와 같은 계약 — 복원 실패가 앱을 막으면 안 된다. */
  }
};
